import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useRef, useState } from 'react';
import ProgressBar from './ProgressBar';
import styles from './Trees.module.css';
import TreesHeaderText from './TreesHeaderText';
import TreeSlide from './TreeSlide';
import TreeStats from './TreeStats';

const SLIDES_COUNT = 4;
const AUTO_ROTATE_DELAY = 4000;
const INITIAL_DELAY = 2500;
const RESTART_DELAY = 3000;
const DEFAULT_TREE_GOAL = 9000000;
const TREE_INCREMENT = 5000000;

interface SlideData {
  id: number;
  value: string;
  label: string;
  description: ReactNode;
  className?: string;
}

const slideData: SlideData[] = [
  {
    id: 1,
    value: '631',
    label: 'hectares',
    description: (
      <>
        hectares of <br />
        land restored
      </>
    ),
  },
  {
    id: 2,
    value: '44%',
    label: 'increase',
    description: (
      <>
        Increase in <br />
        food-secure
        <br />
        families
      </>
    ),
  },
  {
    id: 3,
    value: '1,181,995',
    label: 'tonnes',
    description: (
      <>
        tonnes of CO₂ sequestered
        <br />
        once trees reach maturity
      </>
    ),
  },
  {
    id: 4,
    value: '',
    label: 'mangroves',
    description: (
      <>
        MANGROVES have extensive root systems that{' '}
        <strong>stabilise shorelines</strong> and trap sediments
      </>
    ),
  },
];

const Trees = observer(() => {
  const { isMobile } = useIsMobile();
  const userStore = useUserStore();
  const { treesPlantedCount } = useDrawStore();
  const [treeGoal, setTreeGoal] = useState(DEFAULT_TREE_GOAL);
  const [showSlideInfo, setShowSlideInfo] = useState<number | null>(null);

  const autoRotateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userControlTimerRef = useRef<NodeJS.Timeout | null>(null);
  const initialDelayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const restartDelayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isAutoRotatingRef = useRef(false);
  const isUserControlledRef = useRef(false);
  const hasFadedInRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const showEnterButton = !userStore.subscriptionStatusInfo.subscriberStatus;

  // Update tree goal based on planted count
  useEffect(() => {
    if (treesPlantedCount) {
      const nextGoal =
        Math.ceil(treesPlantedCount / TREE_INCREMENT) * TREE_INCREMENT;
      setTreeGoal(nextGoal);
    }
  }, [treesPlantedCount]);

  const getNextSlide = (slideNum: number): number => {
    return slideNum === SLIDES_COUNT ? 1 : slideNum + 1;
  };

  const clearAllTimers = () => {
    if (autoRotateTimerRef.current) clearTimeout(autoRotateTimerRef.current);
    if (userControlTimerRef.current) clearTimeout(userControlTimerRef.current);
    if (initialDelayTimerRef.current)
      clearTimeout(initialDelayTimerRef.current);
    if (restartDelayTimerRef.current)
      clearTimeout(restartDelayTimerRef.current);
  };

  const startAutoRotation = (slideNum: number) => {
    if (autoRotateTimerRef.current) {
      clearTimeout(autoRotateTimerRef.current);
    }

    setShowSlideInfo(slideNum);

    autoRotateTimerRef.current = setTimeout(() => {
      const nextSlide = getNextSlide(slideNum);
      if (isAutoRotatingRef.current && !isUserControlledRef.current) {
        startAutoRotation(nextSlide);
      }
    }, AUTO_ROTATE_DELAY);
  };

  // Initialize auto-rotation on mount
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFadedInRef.current) {
          hasFadedInRef.current = true;
          isAutoRotatingRef.current = true;
          initialDelayTimerRef.current = setTimeout(() => {
            startAutoRotation(1);
          }, INITIAL_DELAY);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
      if (initialDelayTimerRef.current) {
        clearTimeout(initialDelayTimerRef.current);
      }
    };
  }, []);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  const handleSlideClick = (slideNum: number) => {
    if (showSlideInfo === slideNum) {
      // Close the current slide
      setShowSlideInfo(null);
      isUserControlledRef.current = false;

      const nextSlide = getNextSlide(slideNum);

      if (autoRotateTimerRef.current) {
        clearTimeout(autoRotateTimerRef.current);
      }
      if (restartDelayTimerRef.current) {
        clearTimeout(restartDelayTimerRef.current);
      }

      // Restart auto-rotation
      if (isAutoRotatingRef.current && hasFadedInRef.current) {
        restartDelayTimerRef.current = setTimeout(() => {
          startAutoRotation(nextSlide);
        }, RESTART_DELAY);
      }
    } else {
      // Open a new slide
      setShowSlideInfo(slideNum);
      isUserControlledRef.current = true;

      if (autoRotateTimerRef.current) {
        clearTimeout(autoRotateTimerRef.current);
      }
      if (userControlTimerRef.current) {
        clearTimeout(userControlTimerRef.current);
      }

      // Auto-close after timeout
      userControlTimerRef.current = setTimeout(() => {
        setShowSlideInfo(null);
        isUserControlledRef.current = false;

        const nextSlide = getNextSlide(slideNum);

        if (isAutoRotatingRef.current && hasFadedInRef.current) {
          restartDelayTimerRef.current = setTimeout(() => {
            startAutoRotation(nextSlide);
          }, RESTART_DELAY);
        }
      }, AUTO_ROTATE_DELAY);
    }
  };

  return (
    <AboutUsModule background="white" withMorePadding>
      <AboutUsSiteContainer styles2026>
        <div className={styles.trees} ref={containerRef}>
          <header className={styles.header}>
            {!isMobile && <TreesHeaderText isMobile={isMobile} />}
            <FadeIn onScroll>
              <div className={styles.headerBackground}>
                <div className={styles.gradient} />
                <div className={styles.contentWrapper}>
                  {isMobile && <TreesHeaderText isMobile={isMobile} />}
                  <div className={styles.countWrapper}>
                    <TreeStats
                      value={treesPlantedCount}
                      label="Trees planted"
                    />
                    <TreeStats value={treeGoal} label="Goal" />
                  </div>
                </div>
                <ProgressBar plantedCount={treesPlantedCount} goal={treeGoal} />
              </div>
            </FadeIn>
          </header>

          <section className={styles.treeInfoSlides}>
            {slideData.map((slide, index) => (
              <TreeSlide
                key={slide.id}
                slideNum={slide.id}
                isActive={showSlideInfo === slide.id}
                value={slide.value}
                description={slide.description}
                onSlideClick={handleSlideClick}
                delay={0.2 + index * 0.1}
              />
            ))}
          </section>

          {showEnterButton && (
            <FadeIn onScroll>
              <div className={styles.joinNow}>
                <EnterButton
                  overrideButtonText="Join Now"
                  overrideReferralText
                  dm2026Button
                  buttonSize="Large"
                />
              </div>
            </FadeIn>
          )}
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
});

export default Trees;
